import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { products } from '../mock';
import { X, CheckCircle, Package } from 'lucide-react';

const Products = () => {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState(products);

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setActiveCategory(categoryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === activeCategory));
    }
  }, [activeCategory]);

  const categories = [
    { id: 'all', name: 'Tümü' },
    { id: 'süt', name: 'Süt Ürünleri' },
    { id: 'içecek', name: 'Taze İçecekler' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Ürünlerimiz
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Doğal ve taze ürünlerimizle lezzetin âlâsını keşfedin
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-full shadow-md p-2 space-x-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-[#216DB8] to-[#1a5a9e] text-white shadow-lg'
                    : 'text-gray-700 hover:bg-blue-50'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product) => {
            // Check if product has "Okul Gıdası" certification
            const hasSchoolCert = product.features?.some(f => f.includes('Okul Gıdası'));
            
            return (
              <div
                key={product.id}
                onClick={() => setSelectedProduct(product)}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer transform hover:-translate-y-2"
              >
                <div className="bg-white p-8 flex items-center justify-center h-80 relative">
                  {/* School Certification Badge */}
                  {hasSchoolCert && (
                    <div className="absolute top-4 left-4 z-10 group/badge">
                      <div className="bg-white rounded-lg shadow-lg p-2 transform transition-all duration-300 hover:scale-110">
                        <img
                          src="https://customer-assets.emergentagent.com/job_pekalasut-portal/artifacts/3vq295lj_okul%20g%C4%B1das%C4%B1.png"
                          alt="Okul Gıdası Onaylı"
                          className="h-12 w-12 object-contain"
                        />
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover/badge:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                        Okul Gıdası Onaylı
                      </div>
                    </div>
                  )}
                  
                  <img
                    src={product.image}
                    alt={product.name}
                    className="h-full w-auto object-contain group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 border-t border-gray-100">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                    <span className="text-sm font-medium text-[#216DB8] bg-blue-50 px-3 py-1 rounded-full">
                      {product.size}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                  <div className="flex items-center text-[#216DB8] font-semibold">
                    <span className="text-sm">Detayları Görüntüle</span>
                    <svg className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedProduct(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900">{selectedProduct.name}</h2>
              <button
                onClick={() => setSelectedProduct(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-300"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Product Image */}
                <div className="bg-white rounded-2xl p-8 flex items-center justify-center relative">
                  {/* School Certification Badge in Modal */}
                  {selectedProduct.features?.some(f => f.includes('Okul Gıdası')) && (
                    <div className="absolute top-4 left-4 z-10">
                      <div className="bg-white rounded-lg shadow-lg p-3">
                        <img
                          src="https://customer-assets.emergentagent.com/job_pekalasut-portal/artifacts/3vq295lj_okul%20g%C4%B1das%C4%B1.png"
                          alt="Okul Gıdası Onaylı"
                          className="h-16 w-16 object-contain"
                        />
                        <p className="text-xs text-center text-gray-700 font-semibold mt-1">Onaylı</p>
                      </div>
                    </div>
                  )}
                  
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    className="h-96 w-auto object-contain"
                  />
                </div>

                {/* Product Info */}
                <div>
                  <div className="mb-6">
                    <span className="inline-block text-sm font-medium text-[#216DB8] bg-blue-50 px-4 py-2 rounded-full mb-4">
                      {selectedProduct.size}
                    </span>
                    <p className="text-lg italic text-[#216DB8] mb-4">"{selectedProduct.slogan}"</p>
                    <p className="text-gray-700 leading-relaxed">{selectedProduct.description}</p>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-[#216DB8] mr-2" />
                      Özellikler
                    </h3>
                    <ul className="space-y-2">
                      {selectedProduct.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-[#216DB8] rounded-full mt-2 mr-3 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Ingredients */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <Package className="h-5 w-5 text-[#216DB8] mr-2" />
                      İçindekiler
                    </h3>
                    <p className="text-gray-700 text-sm bg-gray-50 p-4 rounded-lg">{selectedProduct.ingredients}</p>
                  </div>

                  {/* Nutrition */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Besin Değerleri (100g)</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-gray-200">
                          {Object.entries(selectedProduct.nutrition).map(([key, value]) => (
                            <tr key={key}>
                              <td className="py-2 text-gray-700 capitalize">
                                {key === 'energy' ? 'Enerji' : key === 'protein' ? 'Protein' : key === 'carbs' ? 'Karbonhidrat' : key === 'fat' ? 'Yağ' : key === 'calcium' ? 'Kalsiyum' : key === 'sodium' ? 'Sodyum' : key}
                              </td>
                              <td className="py-2 text-right font-medium text-gray-900">{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
